/**
 * Reconciles the module-level privacy store with the server copy.
 *
 * The store (`store.ts`) is deliberately synchronous and offline — it is read
 * by plain formatter functions before React exists. This component is the
 * thing that gives it a server: it fetches the signed-in user's settings once
 * per session and writes local changes back.
 *
 * ORDER MATTERS, AND IT IS THE SAFE DIRECTION
 * --------------------------------------------
 * localStorage wins the FIRST paint (module load), the server wins after the
 * fetch resolves. That order means a user whose stored setting is
 * `hideValues: true` never sees a frame with amounts in it, even if the fetch
 * is slow or fails. The reverse order — defaults first, server second — would
 * flash every amount on the dashboard for as long as the round-trip takes,
 * which is the one moment the feature exists to cover.
 *
 * A user who has never saved anything gets the server's defaults, which are
 * the same defaults the store already holds, so the reconcile is a no-op and
 * nothing flickers.
 *
 * SIGN-OUT CLEARS IT
 * ------------------
 * Privacy settings are per USER; localStorage is per BROWSER. When the session
 * goes away the cached copy goes with it, so the next person to sign in on this
 * machine does not inherit the last one's preferences (or, worse, their
 * un-hidden view).
 */
import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { fetchPrivacy, savePrivacy } from "./api";
import { clearReveal } from "./reveal";
import { resetPrivacy, setPrivacy, type PrivacySettings } from "./store";

type SaveFn = (patch: Partial<PrivacySettings>) => void;

const SaveContext = createContext<SaveFn>(() => {});

/**
 * Apply a change locally AND persist it.
 *
 * Optimistic on purpose: the toggle must feel instant, and the local store is
 * the authority for rendering anyway. A failed write leaves the setting applied
 * for this session and simply not durable — which is strictly better than a
 * toggle that visibly bounces back while somebody is standing over the screen.
 */
export function useSavePrivacy(): SaveFn {
  return useContext(SaveContext);
}

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    if (status !== "authenticated" || !user) return;
    // Refetch when the signed-in user CHANGES, not on every render of a
    // provider that sits above the whole app.
    if (loadedFor.current === user.id) return;
    loadedFor.current = user.id;

    let cancelled = false;
    fetchPrivacy()
      .then((remote) => {
        if (!cancelled) setPrivacy(remote);
      })
      .catch(() => {
        // Keep the local copy. A settings fetch that fails must not un-hide
        // amounts, and must not stop the app from painting.
      });
    return () => {
      cancelled = true;
    };
  }, [status, user]);

  useEffect(() => {
    if (status !== "unauthenticated") return;
    loadedFor.current = null;
    clearReveal();
    resetPrivacy();
  }, [status]);

  const save = useCallback<SaveFn>((patch) => {
    const next = setPrivacy(patch);
    savePrivacy(next).catch(() => {
      /* see the note above: applied now, durable later (or never) */
    });
  }, []);

  return <SaveContext.Provider value={save}>{children}</SaveContext.Provider>;
}
